import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  computed,
  input,
  effect,
  signal,
  untracked,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, UntypedFormControl, ReactiveFormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';

import { GalaxyCheckboxConfigInterface } from './interface';

@Component({
  selector: 'glxy-checkbox',
  templateUrl: './checkbox.component.html',
  styleUrls: ['./checkbox.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatCheckboxModule, ReactiveFormsModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CheckboxComponent),
      multi: true,
    },
  ],
  host: {
    class: 'glxy-checkbox',
  },
})
export class CheckboxComponent implements ControlValueAccessor {
  /** Id of the checkbox */
  id = input<string>('');
  /** The form control for the input. If no form control is passed in, it will create its own */
  formControl = input<UntypedFormControl | undefined>();
  /** The label for the checkbox. If one is not provided, it will use the value passed in via ng-content */
  label = input<string | undefined>();
  /** Sets the disabled state of the checkbox */
  disabled = input(false, { transform: booleanAttribute });
  config = input<GalaxyCheckboxConfigInterface | undefined>();

  protected inputValue = signal(false);
  private isDisabled = signal(false);
  protected computedFormControl = signal<UntypedFormControl>(new UntypedFormControl(false));
  protected computedLabel = computed(() => this.config()?.label || this.label());
  protected computedDisabled = computed(() => this.config()?.disabled || this.disabled());

  constructor() {
    // Setup the form control when config or formControl changes
    effect(() => {
      const configControl = this.config()?.formControl;
      const inputControl = this.formControl();
      const control = configControl || inputControl || new UntypedFormControl(untracked(this.inputValue));

      this.computedFormControl.set(control);
    });

    // Handle disabled state separately
    effect(() => {
      this.setDisabledState(this.computedDisabled());
    });
  }

  // Disables eslint on next line since the onChange function is meant to be overridden
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onChange = (_value: boolean): void => {
    // This should be implemented to correctly implement ControlValueAccessor
  };

  // Disables eslint on next line since the onTouched function is meant to be overridden
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onTouched = (_value: boolean): void => {
    // This should be implemented to correctly implement ControlValueAccessor
  };

  writeValue(value: boolean): void {
    this.inputValue.set(value);
    this.onChange(value);
  }

  registerOnChange(fn: (value: boolean) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: (value: boolean) => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    const control = this.computedFormControl();

    // Do nothing when no change
    if (isDisabled === control.disabled) {
      return;
    }

    this.isDisabled.set(isDisabled);

    if (isDisabled) {
      control.disable();
    } else {
      control.enable();
    }
  }
}
